import { Link } from 'react-router-dom';
import AnnotationStylePicker from '../../../components/AnnotationStylePicker';
import Tooltip from '../../../components/Tooltip';
import styles from './RoiCategoriesConfigSection.module.css';

/**
 * Shared project ROI type editor: list of labels + rectangle stroke styles.
 * Stored under annotationUI.config.roi (enabled, roiCategories[]).
 *
 * @param {{ roi: object, onRoiChange: (nextRoi: object) => void, roiEditorChrome?: object, showIntro?: boolean }} props
 */
export function RoiCategoriesConfigSection({
    roi,
    onRoiChange,
    roiEditorChrome = null,
    showIntro = true,
}) {
    const r = roi ?? { enabled: false, roiCategories: [] };
    const roiCategories = Array.isArray(r.roiCategories) ? r.roiCategories : [];
    const chrome = roiEditorChrome ?? {};
    const {
        folderId = '',
        firstItemId = null,
        hasUnsavedSettings = false,
        showRoiEditorLink = false,
        draftNavBlockedTitle = '',
    } = chrome;

    function updateRoi(patch) {
        onRoiChange({ ...r, ...patch });
    }

    /** Changing ROI setup invalidates admin attestation. */
    function invalidateRoiAttestation(patch) {
        updateRoi({ ...patch, adminRoiDrawingComplete: false });
    }

    function updateRoiCategory(index, patch) {
        const next = roiCategories.map((c, i) => (i === index ? { ...c, ...patch } : c));
        updateRoi({ roiCategories: next });
    }

    function addRoiCategory() {
        const newCat = {
            id: `roi-${Date.now()}`,
            label: '',
        };
        invalidateRoiAttestation({ roiCategories: [...roiCategories, newCat] });
    }

    function removeRoiCategory(index) {
        invalidateRoiAttestation({ roiCategories: roiCategories.filter((_, i) => i !== index) });
    }

    return (
        <div className={styles.fieldGroup}>
            {showIntro && (
                <>
                    <span className={styles.fieldLabel}>Regions of interest (optional)</span>
                    <p className={styles.hint}>
                        Define one or more ROI types (label + outline style). ROIs are drawn separately by a
                        project admin and appear as read-only guidance for annotators. Geometry is stored in a
                        dedicated project annotation layer.
                    </p>
                </>
            )}
            <label className={styles.radioLabel}>
                <input
                    type="checkbox"
                    checked={Boolean(r.enabled)}
                    onChange={(e) => {
                        const on = e.target.checked;
                        updateRoi({
                            enabled: on,
                            ...(!on ? { adminRoiDrawingComplete: false } : {}),
                        });
                    }}
                />
                Enable ROI overlay and navigator for this project
            </label>
            {r.enabled && (
                <>
                    <div className={styles.categoriesHeader}>
                        <span className={styles.subLabel}>ROI types</span>
                        <button type="button" className={styles.addButton} onClick={addRoiCategory}>
                            + Add ROI type
                        </button>
                    </div>
                    {roiCategories.length === 0 && (
                        <p className={styles.empty}>No ROI types defined. Add at least one to use ROIs.</p>
                    )}
                    {roiCategories.map((c, i) => (
                        <div key={c.id || i} className={styles.categoryBlock}>
                            <div className={styles.categoryRow}>
                                <input
                                    type="text"
                                    className={styles.categoryLabel}
                                    value={c.label}
                                    placeholder="Label"
                                    onChange={(e) => updateRoiCategory(i, { label: e.target.value })}
                                />
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => removeRoiCategory(i)}
                                    title="Remove ROI type"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className={styles.stylePickerWrap}>
                                <AnnotationStylePicker
                                    fields={['strokeColor', 'strokeWidth']}
                                    value={c.style}
                                    onChange={(partial) =>
                                        updateRoiCategory(i, { style: { ...(c.style ?? {}), ...partial } })
                                    }
                                />
                            </div>
                        </div>
                    ))}
                    {roiCategories.length > 0 && (
                        <label className={styles.radioLabel}>
                            <input
                                type="checkbox"
                                checked={Boolean(r.adminRoiDrawingComplete)}
                                onChange={(e) => updateRoi({ adminRoiDrawingComplete: e.target.checked })}
                            />
                            I confirm project ROIs are drawn for all slides that need them
                        </label>
                    )}
                    {showRoiEditorLink && (
                        <div className={styles.editRoiRow}>
                            {!firstItemId && (
                                <p className={styles.editRoiHint}>
                                    Add at least one project item to edit ROIs on a slide.
                                </p>
                            )}
                            {firstItemId && hasUnsavedSettings && (
                                <Tooltip text={draftNavBlockedTitle} position="bottom">
                                    <span className={styles.editRoiTooltipWrap}>
                                        <span
                                            className={styles.editRoiFakeButton}
                                            role="button"
                                            tabIndex={0}
                                            aria-disabled="true"
                                        >
                                            Edit ROIs on slide
                                        </span>
                                    </span>
                                </Tooltip>
                            )}
                            {firstItemId && !hasUnsavedSettings && folderId && (
                                <Link
                                    className={styles.editRoiLink}
                                    to={`/projects/${folderId}/roi-edit/${firstItemId}`}
                                >
                                    Edit ROIs on slide
                                </Link>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
